import type { SupportedLocale } from "@/i18n/locale";
import { isApprovedNextImageRemoteSource } from "@/imageRemotePolicy.mjs";
import type {
  LocationProfilePortfolioItemV2,
  LocationProfilePortfolioServiceReference,
  LocationProfileSnapshotV2,
} from "@/app/locations/locationProfile";

import { LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS } from "./locationPortfolioPolicy";

export interface LocationServicePortfolioImage {
  readonly alt: { readonly en: string; readonly ro: string };
  readonly caption: { readonly en: string; readonly ro: string } | null;
  readonly id: string;
  readonly locationId: string;
  readonly service: LocationProfilePortfolioServiceReference;
  readonly src: string;
}

export interface LocalizedServicePortfolioImage {
  readonly alt: string;
  readonly caption: string | null;
  readonly id: string;
  readonly serviceId: string;
  readonly src: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readDenseArraySnapshot<T>(value: readonly T[]): readonly T[] | null;
function readDenseArraySnapshot(value: unknown): readonly unknown[] | null;
function readDenseArraySnapshot(value: unknown): readonly unknown[] | null {
  try {
    if (!Array.isArray(value)) {
      return null;
    }

    const length = value.length;
    const snapshot = new Array<unknown>(length);

    for (let index = 0; index < length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) {
        return null;
      }

      snapshot[index] = value[index];
    }

    if (value.length !== length) {
      return null;
    }

    for (let index = 0; index < length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) {
        return null;
      }
    }

    return Object.freeze(snapshot);
  } catch {
    return null;
  }
}

function isCanonicalText(
  value: unknown,
  maximumLength: number,
): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximumLength
  );
}

function isCanonicalHttpsImageSource(value: unknown): value is string {
  if (!isApprovedNextImageRemoteSource(value)) return false;

  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function detachLocalizedText(
  value: unknown,
  maximumLength: number,
): { readonly en: string; readonly ro: string } | null {
  try {
    if (!isRecord(value) || Object.keys(value).length !== 2) {
      return null;
    }

    const en = value.en;
    const ro = value.ro;

    if (
      !isCanonicalText(en, maximumLength) ||
      !isCanonicalText(ro, maximumLength)
    ) {
      return null;
    }

    return Object.freeze({ en, ro });
  } catch {
    return null;
  }
}

function detachLocationServicePortfolioImage(
  value: unknown,
): LocationServicePortfolioImage | null {
  try {
    if (!isRecord(value)) {
      return null;
    }

    const id = value.id;
    const locationId = value.locationId;
    const src = value.src;
    const altValue = value.alt;
    const captionValue = value.caption;
    const serviceValue = value.service;

    if (!isRecord(serviceValue)) {
      return null;
    }

    const catalogVersion = serviceValue.catalogVersion;
    const serviceId = serviceValue.serviceId;
    const serviceName = serviceValue.serviceName;
    const categoryId = serviceValue.categoryId;
    const bookingHref = serviceValue.bookingHref;
    const alt = detachLocalizedText(altValue, 300);
    const caption =
      captionValue === null
        ? null
        : detachLocalizedText(captionValue, 1_000);

    if (
      !isCanonicalText(id, 160) ||
      !isCanonicalText(locationId, 160) ||
      !isCanonicalHttpsImageSource(src) ||
      !alt ||
      (captionValue !== null && !caption) ||
      !isCanonicalText(catalogVersion, 160) ||
      !isCanonicalText(serviceId, 160) ||
      !isCanonicalText(serviceName, 500) ||
      (categoryId !== null && !isCanonicalText(categoryId, 160)) ||
      !isCanonicalText(bookingHref, 2_000) ||
      !bookingHref.startsWith("/")
    ) {
      return null;
    }

    return Object.freeze({
      alt,
      caption,
      id,
      locationId,
      service: Object.freeze({
        bookingHref,
        catalogVersion,
        categoryId,
        serviceId,
        serviceName,
      }),
      src,
    });
  } catch {
    return null;
  }
}

function detachLocationServicePortfolioImages(
  value: unknown,
): readonly LocationServicePortfolioImage[] | null {
  const sourceItems = readDenseArraySnapshot(value);

  if (
    !sourceItems ||
    sourceItems.length > LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS
  ) {
    return null;
  }

  try {
    const itemIds = new Set<string>();
    const sources = new Set<string>();
    const items: LocationServicePortfolioImage[] = [];

    for (const sourceItem of sourceItems) {
      const item = detachLocationServicePortfolioImage(sourceItem);

      if (
        !item ||
        itemIds.has(item.id) ||
        sources.has(item.src)
      ) {
        return null;
      }

      itemIds.add(item.id);
      sources.add(item.src);
      items.push(item);
    }

    return Object.freeze(items);
  } catch {
    return null;
  }
}

/** Runtime guard for injected provider bytes; malformed media means no gallery. */
export function isValidLocationServicePortfolioImages(
  value: unknown,
): value is readonly LocationServicePortfolioImage[] {
  return detachLocationServicePortfolioImages(value) !== null;
}

/**
 * Projects only exact, tagged Service Portfolio rows. Untagged venue work and
 * Professional Portfolio rows intentionally stay outside service details.
 */
export function resolveLocationServicePortfolioImages(
  items: readonly LocationProfilePortfolioItemV2[],
): readonly LocationServicePortfolioImage[] {
  const sourceItems = readDenseArraySnapshot(items);

  if (
    !sourceItems ||
    sourceItems.length > LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS
  ) {
    return Object.freeze([]);
  }

  try {
    const projected: unknown[] = [];

    for (const sourceItem of sourceItems) {
      if (!isRecord(sourceItem)) {
        return Object.freeze([]);
      }

      const source = sourceItem.source;
      if (!isRecord(source)) {
        return Object.freeze([]);
      }

      const kind = source.kind;
      if (kind !== "service") {
        continue;
      }

      const service = source.service;
      if (service === null) {
        continue;
      }
      if (!isRecord(service)) {
        return Object.freeze([]);
      }

      projected.push({
        alt: sourceItem.alt,
        caption: sourceItem.caption,
        id: sourceItem.itemId,
        locationId: sourceItem.locationId,
        service: {
          bookingHref: service.bookingHref,
          catalogVersion: service.catalogVersion,
          categoryId: service.categoryId,
          serviceId: service.serviceId,
          serviceName: service.serviceName,
        },
        src: sourceItem.src,
      });
    }

    return detachLocationServicePortfolioImages(projected) ?? Object.freeze([]);
  } catch {
    return Object.freeze([]);
  }
}

export function resolveLocationSnapshotServicePortfolioImages(
  profile: Pick<LocationProfileSnapshotV2, "portfolioItems">,
): readonly LocationServicePortfolioImage[] {
  try {
    const portfolioItems = profile.portfolioItems;
    return resolveLocationServicePortfolioImages(portfolioItems);
  } catch {
    return Object.freeze([]);
  }
}

/**
 * Exact-binds tagged media to the catalog document that will render it. One
 * stale/cross-catalog row withdraws the complete modal-media descendant.
 */
export function bindLocationServicePortfolioImagesToCatalog(
  items: readonly LocationServicePortfolioImage[],
  catalog: {
    readonly catalogVersion: string;
    readonly locationId: string;
    readonly services: readonly { readonly serviceId: string }[];
  },
): readonly LocationServicePortfolioImage[] {
  try {
    const detachedItems = detachLocationServicePortfolioImages(items);
    const catalogVersion = catalog.catalogVersion;
    const locationId = catalog.locationId;
    const serviceRows = readDenseArraySnapshot(catalog.services);

    if (
      !detachedItems ||
      !isCanonicalText(catalogVersion, 160) ||
      !isCanonicalText(locationId, 160) ||
      !serviceRows
    ) {
      return Object.freeze([]);
    }

    const serviceIdValues = serviceRows.map((service) => {
      if (!isRecord(service)) {
        return null;
      }

      return service.serviceId;
    });

    if (
      serviceIdValues.some(
        (serviceId) => !isCanonicalText(serviceId, 160),
      )
    ) {
      return Object.freeze([]);
    }

    const serviceIds = new Set(serviceIdValues as string[]);

    if (
      serviceIds.size !== serviceRows.length ||
      detachedItems.some(
        (item) =>
          item.locationId !== locationId ||
          item.service.catalogVersion !== catalogVersion ||
          !serviceIds.has(item.service.serviceId),
      )
    ) {
      return Object.freeze([]);
    }

    return detachedItems;
  } catch {
    return Object.freeze([]);
  }
}

/** Assignment projection after the complete Location publication was bound. */
export function filterLocationServicePortfolioImagesByServiceIds(
  items: readonly LocationServicePortfolioImage[],
  serviceIds: readonly string[],
): readonly LocationServicePortfolioImage[] {
  const detachedItems = detachLocationServicePortfolioImages(items);
  const serviceIdSnapshot = readDenseArraySnapshot(serviceIds);

  if (!detachedItems || !serviceIdSnapshot) {
    return Object.freeze([]);
  }

  try {
    if (
      serviceIdSnapshot.some(
        (serviceId) => !isCanonicalText(serviceId, 160),
      ) ||
      new Set(serviceIdSnapshot).size !== serviceIdSnapshot.length
    ) {
      return Object.freeze([]);
    }

    const allowed = new Set(serviceIdSnapshot as readonly string[]);
    return Object.freeze(
      detachedItems.filter((item) => allowed.has(item.service.serviceId)),
    );
  } catch {
    return Object.freeze([]);
  }
}

/** Exact service-ID join used by Location and Specialist service modals. */
export function getLocalizedServicePortfolioImages(
  items: readonly LocationServicePortfolioImage[],
  serviceId: string,
  locale: SupportedLocale,
): readonly LocalizedServicePortfolioImage[] {
  const detachedItems = detachLocationServicePortfolioImages(items);

  if (
    !detachedItems ||
    !isCanonicalText(serviceId, 160) ||
    (locale !== "en" && locale !== "ro")
  ) {
    return Object.freeze([]);
  }

  try {
    const seenSources = new Set<string>();

    return Object.freeze(
      detachedItems.flatMap((item): LocalizedServicePortfolioImage[] => {
        if (item.service.serviceId !== serviceId || seenSources.has(item.src)) {
          return [];
        }

        seenSources.add(item.src);
        return [
          Object.freeze({
            alt: item.alt[locale],
            caption: item.caption?.[locale] ?? null,
            id: item.id,
            serviceId: item.service.serviceId,
            src: item.src,
          }),
        ];
      }),
    );
  } catch {
    return Object.freeze([]);
  }
}
